import React, { useState, useEffect } from 'react';
import { 
  Search, CheckCircle, XCircle, Users, Calendar, 
  BookOpen, Clock, Save, AlertCircle, ChevronDown,
  FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExamAttendance = () => {
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [existingAttendance, setExistingAttendance] = useState(null);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('staffToken')}`,
    'Content-Type': 'application/json'
  });

  // Fetch teacher's classes on mount
  useEffect(() => {
    fetchMyClasses();
  }, []);

  const fetchMyClasses = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/staff-portal/my-classes`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // Handle auth error - redirect to login
        if (response.status === 401) {
          toast.error('Please login again');
          return;
        }
        throw new Error('Failed to fetch classes');
      }
      
      const data = await response.json();
      // Ensure we always set an array
      const classList = Array.isArray(data.classes) ? data.classes : 
                        Array.isArray(data) ? data : [];
      setClasses(classList);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to load classes');
      setClasses([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchExamsForClass = async (classItem) => {
    setSelectedClass(classItem);
    setSelectedExam(null);
    setStudents([]);
    setAttendance({});
    setExams([]);
    
    try {
      const response = await fetch(`${API_URL}/api/staff-portal/my-classes/${classItem.id}/exams`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setExams(data.exams || []);
      if (data.exams?.length === 0) {
        toast.info('No exams found for this class');
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      toast.error('Failed to load exams');
    }
  };

  const fetchStudentsAndAttendance = async (exam) => {
    setSelectedExam(exam);
    setLoading(true);
    
    try {
      // Fetch students for the class
      const studentsResponse = await fetch(`${API_URL}/api/staff-portal/my-classes/${selectedClass.id}/students`, {
        headers: getAuthHeaders()
      });
      const studentsData = await studentsResponse.json();
      const studentList = studentsData.students || studentsData || [];
      setStudents(studentList);
      
      // Initialize attendance state
      const initialAttendance = {};
      studentList.forEach(student => {
        initialAttendance[student.id] = 'present'; // Default to present
      });
      
      // Try to fetch existing attendance
      try {
        const attendanceResponse = await fetch(
          `${API_URL}/api/staff-portal/exam-attendance/get?exam_id=${exam.id}&subject_id=${selectedClass.subject_id}`, 
          { headers: getAuthHeaders() }
        );
        const attendanceData = await attendanceResponse.json();
        
        if (attendanceData && attendanceData.attendance && attendanceData.attendance.length > 0) {
          setExistingAttendance(attendanceData);
          // Override with existing attendance
          attendanceData.attendance.forEach(record => {
            if (initialAttendance.hasOwnProperty(record.student_id)) {
              initialAttendance[record.student_id] = record.status;
            }
          });
        }
      } catch (e) {
        // No existing attendance, use defaults
        setExistingAttendance(null);
      }
      
      setAttendance(initialAttendance);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const markAllPresent = () => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = 'present';
    });
    setAttendance(newAttendance);
  };

  const markAllAbsent = () => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = 'absent';
    });
    setAttendance(newAttendance);
  };

  const saveAttendance = async () => {
    if (!selectedExam || !selectedClass) {
      toast.error('Please select exam and class');
      return;
    }

    setSaving(true);
    try {
      const attendanceData = {
        exam_id: selectedExam.id,
        exam_name: selectedExam.name,
        class_id: selectedClass.id,
        course_id: selectedClass.course_id,
        semester_id: selectedClass.semester_id,
        subject_id: selectedClass.subject_id,
        date: new Date().toISOString().split('T')[0],
        attendance: Object.entries(attendance).map(([studentId, status]) => {
          const student = students.find(s => s.id === studentId);
          return {
            student_id: studentId,
            student_name: student?.full_name || student?.name || 'Unknown',
            admission_number: student?.admission_number || '',
            status: status
          };
        })
      };

      const response = await fetch(`${API_URL}/api/staff-portal/exam-attendance/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(attendanceData)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to save attendance');
      }
      
      toast.success('Exam attendance saved successfully!');
      setExistingAttendance(attendanceData);
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error.message || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const name = student.full_name || student.name || '';
    const admNo = student.admission_number || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           admNo.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const presentCount = Object.values(attendance).filter(s => s === 'present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'absent').length;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-7 w-7 text-purple-600" />
            Exam Attendance
          </h1>
          <p className="text-gray-600 mt-1">Mark student attendance for examinations</p>
        </div>

        {/* Step 1: Select Class */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
          <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-sm">Step 1</span>
            Select Class
          </h2>
          
          {loading && classes.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : classes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No classes assigned to you</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {classes.map(classItem => (
                <button
                  key={classItem.id}
                  onClick={() => fetchExamsForClass(classItem)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedClass?.id === classItem.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-medium text-gray-900">{classItem.course_name}</p>
                  <p className="text-sm text-gray-600">
                    {classItem.semester_name} • {classItem.subject_name}
                  </p>
                  {classItem.section_name && (
                    <p className="text-xs text-gray-500 mt-1">Section: {classItem.section_name}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Step 2: Select Exam */}
        {selectedClass && (
          <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
            <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-sm">Step 2</span>
              Select Exam
            </h2>
            
            {exams.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No exams found for this class</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {exams.map(exam => (
                  <button
                    key={exam.id}
                    onClick={() => fetchStudentsAndAttendance(exam)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      selectedExam?.id === exam.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{exam.name}</p>
                    <p className="text-sm text-gray-600">{exam.exam_type || 'Exam'}</p>
                    {exam.date && (
                      <p className="text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {formatDate(exam.date)}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Mark Attendance */}
        {selectedExam && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-sm">Step 3</span>
                    Mark Attendance
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedExam.name} • {students.length} Students
                  </p>
                </div>
                
                {/* Quick Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={markAllPresent}
                    data-testid="mark-all-present-btn"
                    className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                  >
                    All Present
                  </button>
                  <button
                    onClick={markAllAbsent}
                    data-testid="mark-all-absent-btn"
                    className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    All Absent
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="mt-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Stats */}
              <div className="flex gap-4 mt-3">
                <div className="flex items-center gap-1 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 font-medium">{presentCount} Present</span>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 font-medium">{absentCount} Absent</span>
                </div>
              </div>

              {existingAttendance && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Attendance already recorded. You can update it.
                  </p>
                </div>
              )}
            </div>

            {/* Student List */}
            <div className="divide-y max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                </div>
              ) : filteredStudents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No students found</p>
                </div>
              ) : (
                filteredStudents.map((student, index) => (
                  <div
                    key={student.id}
                    className={`flex items-center justify-between p-3 hover:bg-gray-50 ${
                      attendance[student.id] === 'absent' ? 'bg-red-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400 w-6">{index + 1}</span>
                      <div>
                        <p className="font-medium text-gray-900">
                          {student.full_name || student.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {student.admission_number || student.roll_number || 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAttendanceChange(student.id, 'present')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          attendance[student.id] === 'present'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700'
                        }`}
                      >
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Present
                      </button>
                      <button
                        onClick={() => handleAttendanceChange(student.id, 'absent')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          attendance[student.id] === 'absent'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-700'
                        }`}
                      >
                        <XCircle className="h-4 w-4 inline mr-1" />
                        Absent
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Save Button */}
            {students.length > 0 && (
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={saveAttendance}
                  disabled={saving}
                  data-testid="save-exam-attendance-btn"
                  className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-5 w-5" />
                      Save Exam Attendance
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamAttendance;
